import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FastingSession, Language } from '../types';
import {
    getElapsedMs,
    getEndTimestamp,
    getProgressRatio,
    getRemainingMs,
    isGoalReached,
    type FastingUIStatus,
    type IntermittentFastingSnapshot,
} from '../utils/intermittentFasting';
import { getSavedFastingGoalHours, saveFastingGoalHours } from '../utils/fastingPreferences';
import {
    cancelFastingEndNotification,
    cancelFastingStartNotification,
    checkMissedFastingEndNotification,
    checkMissedFastingStartNotification,
    resyncFastingEndNotification,
    resyncFastingStartNotification,
} from '../utils/fastingNotifications';
import {
    activateScheduledFastingSession,
    cancelScheduledFastingSession,
    scheduleFastingSession,
    startFastingSession,
    stopFastingSession,
    subscribeActiveFastingSession,
    subscribeScheduledFastingSession,
    updateFastingSessionStartTime,
} from '../utils/fastingSessions';

export interface UseIntermittentFastingOptions {
    enabled?: boolean;
    language?: Language;
}

export interface UseIntermittentFastingResult {
    snapshot: IntermittentFastingSnapshot;
    progress: number;
    remainingMs: number;
    elapsedMs: number;
    endTimestamp: number | null;
    goalReached: boolean;
    isBusy: boolean;
    startFast: () => Promise<void>;
    scheduleFast: (scheduledStartAt: Date) => Promise<void>;
    cancelScheduledFast: () => Promise<void>;
    endFast: () => Promise<void>;
    updateStartTime: (newStartedAt: Date) => Promise<void>;
    setGoalHours: (hours: number) => void;
}

export function useIntermittentFasting(
    userId: string,
    options: UseIntermittentFastingOptions = {},
): UseIntermittentFastingResult {
    const { enabled = true, language } = options;
    const [activeSession, setActiveSession] = useState<FastingSession | null>(null);
    const [scheduledSession, setScheduledSession] = useState<FastingSession | null>(null);
    const [goalHoursPref, setGoalHoursPref] = useState(() => getSavedFastingGoalHours(userId));
    const [nowMs, setNowMs] = useState(Date.now());
    const [isBusy, setIsBusy] = useState(false);
    const activatingRef = useRef(false);

    useEffect(() => {
        if (!userId) {
            setActiveSession(null);
            setScheduledSession(null);
            return;
        }
        setGoalHoursPref(getSavedFastingGoalHours(userId));
        const unsubActive = subscribeActiveFastingSession(userId, setActiveSession);
        const unsubScheduled = subscribeScheduledFastingSession(userId, setScheduledSession);
        return () => {
            unsubActive();
            unsubScheduled();
        };
    }, [userId]);

    const startedAt = activeSession ? new Date(activeSession.startedAt).getTime() : null;
    const scheduledStartAt = scheduledSession?.scheduledStartAt
        ? new Date(scheduledSession.scheduledStartAt).getTime()
        : scheduledSession
            ? new Date(scheduledSession.startedAt).getTime()
            : null;

    const status: FastingUIStatus = activeSession
        ? 'active'
        : scheduledSession
            ? 'scheduled'
            : 'idle';

    const goalHours = activeSession?.goalHours
        ?? scheduledSession?.goalHours
        ?? goalHoursPref;

    useEffect(() => {
        if (status === 'idle') return;
        const id = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [status]);

    const snapshot: IntermittentFastingSnapshot = useMemo(
        () => ({ status, goalHours, startedAt, scheduledStartAt, nowMs }),
        [status, goalHours, startedAt, scheduledStartAt, nowMs],
    );

    const goalReached = isGoalReached(snapshot);


    useEffect(() => {
        if (!enabled || !activeSession) {
            cancelFastingEndNotification();
            return;
        }
        resyncFastingEndNotification(activeSession, goalHours, language);
    }, [enabled, activeSession, goalHours, language]);

    useEffect(() => {
        if (!enabled || !scheduledSession) {
            cancelFastingStartNotification();
            return;
        }
        resyncFastingStartNotification(scheduledSession, goalHours, language);
    }, [enabled, scheduledSession, goalHours, language]);

    useEffect(() => {
        if (!userId || !scheduledSession || activeSession || activatingRef.current || isBusy) {
            return;
        }

        const startAtMs = scheduledStartAt ?? Date.now();
        const msUntil = startAtMs - Date.now();

        const activate = async () => {
            if (activatingRef.current) return;
            activatingRef.current = true;
            cancelFastingStartNotification();
            try {
                await activateScheduledFastingSession(userId, scheduledSession);
            } catch (error) {
                console.error('Failed to activate scheduled fast', error);
                activatingRef.current = false;
            }
        };

        if (msUntil <= 0) {
            void activate();
            return;
        }

        const id = window.setTimeout(() => {
            void activate();
        }, msUntil);
        return () => window.clearTimeout(id);
    }, [userId, scheduledSession, scheduledStartAt, activeSession, isBusy]);

    useEffect(() => {
        if (!enabled) return;

        const syncMissed = () => {
            checkMissedFastingEndNotification(activeSession?.id ?? null);
            checkMissedFastingStartNotification(scheduledSession?.id ?? null);
        };

        syncMissed();
        window.addEventListener('focus', syncMissed);
        document.addEventListener('visibilitychange', syncMissed);
        return () => {
            window.removeEventListener('focus', syncMissed);
            document.removeEventListener('visibilitychange', syncMissed);
        };
    }, [enabled, activeSession?.id, scheduledSession?.id]);

    useEffect(() => {
        if (!enabled) {
            cancelFastingEndNotification();
            cancelFastingStartNotification();
        }
    }, [enabled]);

    useEffect(() => {
        if (!scheduledSession) {
            activatingRef.current = false;
        }
    }, [scheduledSession]);

    const startFast = useCallback(async () => {
        if (!userId || !enabled || activeSession || isBusy) return;
        setIsBusy(true);
        try {
            if (scheduledSession) {
                cancelFastingStartNotification();
                await cancelScheduledFastingSession(userId, scheduledSession.id);
            }
            await startFastingSession(userId, goalHoursPref);
        } finally {
            setIsBusy(false);
        }
    }, [userId, enabled, activeSession, scheduledSession, isBusy, goalHoursPref]);

    const scheduleFast = useCallback(async (scheduledStartAt: Date) => {
        if (!userId || !enabled || activeSession || scheduledSession || isBusy) return;
        if (scheduledStartAt.getTime() <= Date.now()) {
            setIsBusy(true);
            try {
                await startFastingSession(userId, goalHoursPref, scheduledStartAt.toISOString());
            } finally {
                setIsBusy(false);
            }
            return;
        }
        setIsBusy(true);
        try {
            await scheduleFastingSession(userId, goalHoursPref, scheduledStartAt.toISOString());
        } finally {
            setIsBusy(false);
        }
    }, [userId, enabled, activeSession, scheduledSession, isBusy, goalHoursPref]);

    const cancelScheduledFast = useCallback(async () => {
        if (!userId || !scheduledSession || isBusy) return;
        setIsBusy(true);
        cancelFastingStartNotification();
        try {
            await cancelScheduledFastingSession(userId, scheduledSession.id);
        } finally {
            setIsBusy(false);
        }
    }, [userId, scheduledSession, isBusy]);

    const endFast = useCallback(async () => {
        if (!userId || !activeSession || isBusy) return;
        setIsBusy(true);
        cancelFastingEndNotification();
        try {
            await stopFastingSession(userId, activeSession);
        } finally {
            setIsBusy(false);
        }
    }, [userId, activeSession, isBusy]);

    const updateStartTime = useCallback(async (newStartedAt: Date) => {
        if (!userId || !activeSession || isBusy) return;
        const clamped = new Date(Math.min(newStartedAt.getTime(), Date.now()));
        setIsBusy(true);
        try {
            await updateFastingSessionStartTime(userId, activeSession.id, clamped.toISOString());
        } finally {
            setIsBusy(false);
        }
    }, [userId, activeSession, isBusy]);

    const setGoalHours = useCallback((hours: number) => {
        const clamped = Math.max(1, Math.min(48, hours));
        setGoalHoursPref(clamped);
        if (userId) {
            saveFastingGoalHours(userId, clamped);
        }
    }, [userId]);

    return {
        snapshot,
        progress: getProgressRatio(snapshot),
        remainingMs: getRemainingMs(snapshot),
        elapsedMs: getElapsedMs(snapshot),
        endTimestamp: getEndTimestamp(snapshot),
        goalReached,
        isBusy,
        startFast,
        scheduleFast,
        cancelScheduledFast,
        endFast,
        updateStartTime,
        setGoalHours,
    };
}
